import React, {useCallback, useEffect, useMemo, useState} from 'react';
import toast from 'react-hot-toast';
import {useAuth} from '../../../contexts/UserContext';
import {HeaderContent, StepHeader, StepSubtitle, StepTitle} from '../styles';
import BlocoEndereco from './BlocoEndereco';
import DadosAudiencia from './DadosAudiencia';
import InformacoesBasicas from './InformacoesBasicas';
import {
  SectionContainer,
  StepContainer,
  StepContent,
} from './styles';
import {
  IDadosAudiencia,
  IDadosBasicosNovaDemanda,
  IDadosEnderecoDemanda,
  StepServicoProps
} from './types';

const StepServico: React.FC<StepServicoProps> = ({
  step,
  currentStep,
  setCurrentStep,
  tipoDemanda,
  tituloFormulario,
  setDadosServico,
  validando,
  setValidando,
  setComarca,
  initialDadosServico
}) => {
  const { user } = useAuth();
  const [dadosBasicos, setDadosBasicos] = useState<IDadosBasicosNovaDemanda>(
    initialDadosServico?.dadosBasicos || {
      id_comarca: null,
      numero_processo: '',
      natureza: 'Judicial',
      nome_cliente: '',
      valor_cliente: undefined,
      codigo_cliente: '',
      valor_profissional: undefined,
      data_tz: 'BRT',
      tipo_parte: 'nenhum'
    }
  );

  const [dadosEndereco, setDadosEndereco] = useState<IDadosEnderecoDemanda>(
    initialDadosServico?.endereco || {
      rua: '',
      bairro: '',
      cep: '',
      numero: '',
      complemento: ''
    }
  );

  const [dadosAudiencia, setDadosAudiencia] = useState<IDadosAudiencia | undefined>(
    initialDadosServico?.audiencia
  );

  const [erros, setErros] = useState<any>({});

  // Memoiza os dados do serviço para evitar re-renders desnecessários
  const dadosServico = useMemo(() => ({
    dadosBasicos,
    endereco: dadosEndereco,
    audiencia: dadosAudiencia,
    avulsos: {}
  }), [dadosBasicos, dadosEndereco, dadosAudiencia]);

  useEffect(() => {
    if (!initialDadosServico) return;
    
    setDadosBasicos(initialDadosServico.dadosBasicos);
    setDadosEndereco(initialDadosServico.endereco);
    setDadosAudiencia(initialDadosServico.audiencia);
    if (initialDadosServico.dadosBasicos.id_comarca) {
      setComarca(String(initialDadosServico.dadosBasicos.id_comarca));
    }
  }, [initialDadosServico, setComarca]);

  useEffect(() => {
    setDadosServico(dadosServico);
  }, [dadosServico, setDadosServico]);

  const handleDadosBasicos = useCallback((dados: IDadosBasicosNovaDemanda) => {
    setDadosBasicos(dados);
  }, []);

  const handleDadosEndereco = useCallback((dados: IDadosEnderecoDemanda) => {
    setDadosEndereco(dados);
  }, []);

  const handleDadosAudiencia = useCallback((dados: IDadosAudiencia) => {
    setDadosAudiencia(dados);
  }, []);

  const handleEnderecoVirtualInline = useCallback((valor: string) => {
    setDadosAudiencia(prev => {
      if (prev) {
        return { ...prev, endereco_virtual: valor };
      }
      return { tipo_audiencia: '', profissional_necessario: '', modalidade: '', endereco_virtual: valor } as IDadosAudiencia;
    });
  }, []);

  const handleEstadoComarcaChange = useCallback((uf: string, cidade: string) => {
    if (!dadosBasicos.estado || dadosBasicos.estado.trim() === '') {
      setDadosBasicos(prev => ({
        ...prev,
        estado: uf
      }));
    }
  }, [dadosBasicos.estado]);

  const validarDados = useCallback(() => {
    const novosErros: any = {};

    if (!dadosBasicos.estado?.trim()) {
      novosErros.uf = 'Estado é obrigatório';
    }
    if (!dadosBasicos.id_comarca) {
      novosErros.id_comarca = 'Comarca é obrigatória';
    }
    if (!dadosBasicos.numero_processo.trim()) {
      novosErros.numero_processo = dadosBasicos.natureza === 'Judicial'
        ? 'Número do processo é obrigatório'
        : 'O campo Número de Identificação é obrigatório';
    }
    if (!dadosBasicos.natureza.trim()) {
      novosErros.natureza = 'Natureza é obrigatória';
    }
    if (!dadosBasicos.data) {
      novosErros.data = 'Prazo fatal é obrigatório';
    } else {
      const dataFatal = dadosBasicos.data instanceof Date ? dadosBasicos.data : new Date(dadosBasicos.data);
      
      if (isNaN(dataFatal.getTime())) {
        novosErros.data = 'Data do prazo fatal inválida';
      } else {
        const horas = dataFatal.getHours();
        const minutos = dataFatal.getMinutes();
        const segundos = dataFatal.getSeconds();
        const milissegundos = dataFatal.getMilliseconds();
        
        if (horas === 0 && minutos === 0 && segundos === 0 && milissegundos === 0) {
          novosErros.data = 'Data e hora fatal são obrigatórias';
        } else if (dataFatal < new Date()) {
          novosErros.data = 'Prazo fatal não pode ser anterior à data atual';
        }
      }
    }
    if (user?.profile !== 'Solicitante') {
      if (dadosBasicos.valor_cliente === undefined || dadosBasicos.valor_cliente === null || dadosBasicos.valor_cliente <= 0) {
        novosErros.valor_cliente = 'Valor do cliente é obrigatório e deve ser maior que zero';
      }
      if (dadosBasicos.valor_profissional === undefined || dadosBasicos.valor_profissional === null || dadosBasicos.valor_profissional <= 0) {
        novosErros.valor_profissional = 'Valor do serviço é obrigatório e deve ser maior que zero';
      }
      
      if (dadosBasicos.tipo_parte === 'cliente') {
        if (!dadosBasicos.nome_cliente?.trim()) {
          novosErros.nome_cliente = 'Nome é obrigatório quando cliente está selecionado';
        }
        if (!dadosBasicos.codigo_cliente?.trim()) {
          novosErros.codigo_cliente = 'Código do cliente é obrigatório quando cliente está selecionado';
        }
      }
      
      if (dadosBasicos.tipo_parte === 'solicitante') {
        if (!dadosBasicos.id_escritorio_solicitante) {
          novosErros.id_escritorio_solicitante = 'Solicitante é obrigatório quando solicitante está selecionado';
        }
      }
    }

    if (tipoDemanda === 'Audiência' && dadosAudiencia) {
      if (!dadosAudiencia.tipo_audiencia.trim()) {
        novosErros.tipo_audiencia = 'Tipo é obrigatório';
      }
      if (!dadosAudiencia.modalidade.trim()) {
        novosErros.modalidade = 'Modalidade é obrigatória';
      }
      if (!dadosAudiencia.profissional_necessario.trim()) {
        novosErros.profissional_necessario = 'Profissional necessário é obrigatório';
      }
      if (dadosAudiencia.modalidade === 'Virtual' && !dadosAudiencia.endereco_virtual?.trim()) {
        novosErros.endereco_virtual = 'Endereço virtual é obrigatório para modalidade virtual';
      }
    }

    setErros(novosErros);
    
    if (Object.keys(novosErros).length > 0) {
      toast.error(`Falha na validação dos dados.`)
    }
    
    return Object.keys(novosErros).length === 0;
  }, [dadosBasicos, dadosAudiencia, tipoDemanda, user?.profile]);

  useEffect(() => {
    if (!validando || step !== currentStep) return;
    
    const isValid = validarDados();
    if (isValid) {
      setCurrentStep(currentStep + 1);
    }
    setValidando(false);
  }, [validando, step, currentStep, validarDados, setCurrentStep, setValidando]);

  // Verifica se o step está ativo
  const isActive = currentStep === step;

  if (!isActive) {
    return null;
  }

  return (
    <StepContainer>
      {/* Header do Step */}
      <StepHeader>
        <HeaderContent>
          <StepTitle>
            {tituloFormulario}
          </StepTitle>
          <StepSubtitle>
            Informe os dados para localizarmos o profissional apropriado.
          </StepSubtitle>
        </HeaderContent>
      </StepHeader>

      {/* Conteúdo do Step */}
      <StepContent>
        {/* Seção de Informações Básicas */}
        <SectionContainer>
          <InformacoesBasicas
            tipoDemanda={tipoDemanda}
            tituloFormulario="Informações Básicas"
            erros={erros}
            dadosBasicos={dadosBasicos}
            setDadosBasicos={handleDadosBasicos}
            setComarca={setComarca}
          />
        </SectionContainer>

        {/* Seção de Dados de Audiência - Só aparece para audiências */}
        {tipoDemanda === 'Audiência' && (
          <SectionContainer>
            <DadosAudiencia
              tituloFormulario="Para audiências, informe também:"
              tipoAudienciaEspecifico={tituloFormulario}
              erros={erros}
              setDadosAudiencia={handleDadosAudiencia}
              dadosAudiencia={dadosAudiencia || {
                tipo_audiencia: '',
                profissional_necessario: '',
                modalidade: '',
                endereco_virtual: ''
              }}
            />
          </SectionContainer>
        )}

        {/* Seção de Endereço */}
        <SectionContainer>
          <BlocoEndereco
            erros={erros}
            setDadosEndereco={handleDadosEndereco}
            dadosEndereco={dadosEndereco}
            modalidade={dadosAudiencia?.modalidade}
            enderecoVirtual={dadosAudiencia?.endereco_virtual || ''}
            onChangeEnderecoVirtual={handleEnderecoVirtualInline}
            onEstadoComarcaChange={handleEstadoComarcaChange}
          />
        </SectionContainer>


      </StepContent>

    </StepContainer>
  );
};

export default StepServico;
